import { useState } from "react";
import { PendingSettlements, ReceivableReconcile } from "@/pages/admin/V3";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/format";
import { Field, Kpi, PageHeader, Section, StatusBadge } from "@/components/common/kit";
import DataTable from "@/components/common/DataTable";

function Detail({ sid, onClose, onChange }) {
  const { bizDate } = useAuth();
  const { data, reload } = useFetch(sid ? `/receivables/${sid}` : null);
  const [f, setF] = useState({ amount: "", date: bizDate, remarks: "" });
  const settle = async () => {
    try {
      await api.post(`/receivables/${sid}/settle`, { ...f, amount: Number(f.amount) });
      toast.success("Settlement recorded");
      setF({ amount: "", date: bizDate, remarks: "" });
      reload();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Sheet open={!!sid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="receivable-detail">
        <SheetHeader><SheetTitle>{data?.salesman.full_name} · Receivable</SheetTitle></SheetHeader>
        {data && (
          <div className="mt-4 space-y-5">
            <div className="grid grid-cols-3 gap-2">
              <Kpi label="Total Due" value={inr(data.due)} />
              <Kpi label="Settled" value={inr(data.settled)} tone="green" />
              <Kpi label="Outstanding" value={inr(data.outstanding)} tone="red" testid="rec-outstanding" />
            </div>
            <div className="rounded border border-slate-200 p-3">
              <p className="mb-2 text-sm font-bold">Record settlement</p>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Amount (₹)"><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="settle-amount" /></Field>
                <Field label="Date"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="settle-date" /></Field>
                <div className="col-span-2"><Field label="Remarks"><Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="settle-remarks" /></Field></div>
              </div>
              <Button className="mt-3 w-full" onClick={settle} disabled={!f.amount} data-testid="settle-save-button">Record settlement</Button>
            </div>
            <div><p className="mb-2 text-sm font-bold">Dues history</p>
              <DataTable searchable={false} pageSize={0} rows={data.entries} testid="rec-entries-table" columns={[
                { key: "date", label: "Day", type: "date" }, { key: "amount", label: "Amount", type: "money" },
                { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> }]} />
            </div>
            <div><p className="mb-2 text-sm font-bold">Settlements</p>
              <DataTable searchable={false} pageSize={0} rows={data.settlements} testid="rec-settlements-table" columns={[
                { key: "settlement_no", label: "ID" }, { key: "date", label: "Date", type: "date" }, { key: "amount", label: "Amount", type: "money" },
                { key: "remarks", label: "Remarks" }, { key: "recorded_by", label: "By" }]} />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Receivables() {
  const { data, loading, reload } = useFetch("/receivables");
  const [sid, setSid] = useState(null);
  const total = (data || []).reduce((s, r) => s + r.outstanding, 0);
  return (
    <div className="space-y-6">
      <PageHeader title="Salesman Receivables" subtitle="Amount due from salesmen (cash shortages). This is NOT customer credit." />
      <div className="grid gap-3 sm:grid-cols-3"><Kpi label="Total Outstanding" value={inr(total)} tone="red" testid="rec-total-outstanding" /></div>
      <PendingSettlements onChange={reload} />
      <ReceivableReconcile />
      <Section>
        <DataTable rows={data || []} loading={loading} onRowClick={(r) => setSid(r.id)} totals={["due", "settled", "outstanding"]} title="Salesman Receivables" exportable testid="receivables-table" columns={[
          { key: "code", label: "ID" }, { key: "full_name", label: "Salesman" }, { key: "due", label: "Total Due", type: "money" },
          { key: "settled", label: "Settled", type: "money" }, { key: "outstanding", label: "Outstanding", type: "money" },
          { key: "act", label: "", sortable: false, noExport: true, render: (r) => <Button size="sm" variant="outline" data-testid={`rec-open-${r.code}`}>View / Settle</Button> }]} />
      </Section>
      <Detail sid={sid} onClose={() => setSid(null)} onChange={reload} />
    </div>
  );
}
